
import { BellRing, Check, CheckCheck, Inbox } from 'lucide-react';
import { SettingsIcon } from '@/shared/config';
import { useCallback, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  useNotificationsStore,
  useUnreadCount,
  formatNotificationTitle,
  notificationLink,
} from '@/features/notifications';
import { notificationVisual, NotificationEventIcon } from '@/features/notifications/lib/notification-icon';
import { useAuthStore } from '@/features/auth';
import type { Notification } from '@/entities/notification';
import { Badge, Button, EmptyState, ErrorState, Pagination, Skeleton, Tooltip, PageMotion } from '@/shared/ui';
import { formatDayLabel, formatDateTime, formatRelativeTime } from '@/shared/lib/date';
import { cn } from '@/shared/lib/cn';
import styles from './Notifications.module.css';

const PAGE_SIZE = 20;

type Filter = 'all' | 'unread';

const FILTERS: { key: Filter; label: string; hint: string }[] = [
  { key: 'all', label: 'Все', hint: 'Вся история уведомлений' },
  { key: 'unread', label: 'Непрочитанные', hint: 'Только то, что вы ещё не открывали' },
];

const isUnread = (n: Notification) => !n.readAt && !n.read;

function groupByDay(items: Notification[]): { day: string; items: Notification[] }[] {
  const groups: { day: string; items: Notification[] }[] = [];
  for (const n of items) {
    const day = formatDayLabel(n.createdAt);
    const last = groups[groups.length - 1];
    if (last && last.day === day) last.items.push(n);
    else groups.push({ day, items: [n] });
  }
  return groups;
}

export const NotificationsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const filter: Filter = searchParams.get('filter') === 'unread' ? 'unread' : 'all';
  const page = Math.max(1, Number.parseInt(searchParams.get('page') ?? '1', 10) || 1);

  const patchParams = useCallback(
    (next: { page?: number; filter?: Filter }) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next.filter !== undefined) {
            if (next.filter === 'all') params.delete('filter');
            else params.set('filter', next.filter);
          }
          if (next.page !== undefined) {
            if (next.page <= 1) params.delete('page');
            else params.set('page', String(next.page));
          }
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const userId = useAuthStore((s) => s.user?.id);
  const pageItems = useNotificationsStore((s) => s.pageItems);
  const pageTotal = useNotificationsStore((s) => s.pageTotal);
  const pageLoading = useNotificationsStore((s) => s.pageLoading);
  const pageError = useNotificationsStore((s) => s.pageError);
  const pageLoaded = useNotificationsStore((s) => s.pageLoaded);
  const pendingNew = useNotificationsStore((s) => s.pendingNew);
  const fetchPageList = useNotificationsStore((s) => s.fetchPageList);
  const markAsRead = useNotificationsStore((s) => s.markAsRead);
  const markAllAsRead = useNotificationsStore((s) => s.markAllAsRead);
  const { data: unreadCount = 0 } = useUnreadCount();

  const totalPages = Math.max(1, Math.ceil(pageTotal / PAGE_SIZE));

  const load = useCallback(
    () =>
      fetchPageList({
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
        unreadOnly: filter === 'unread',
      }),
    [fetchPageList, page, filter],
  );

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!pageLoading && !pageError && page > totalPages) patchParams({ page: totalPages });
  }, [page, totalPages, pageLoading, pageError, patchParams]);

  const groups = useMemo(() => groupByDay(pageItems), [pageItems]);
  const showSkeleton = pageLoading && !pageLoaded;

  const handleFilter = (next: Filter) => patchParams({ filter: next, page: 1 });

  return (
    <PageMotion>
      <main className={styles.wrapper}>
        <div className={styles.header}>
          <div className={styles.titleRow}>
            <h1 className={styles.pageTitle}>Уведомления</h1>
            {unreadCount > 0 && (
              <Tooltip content="Непрочитанных всего">
                <Badge variant="primary">{unreadCount > 99 ? '99+' : unreadCount}</Badge>
              </Tooltip>
            )}
          </div>

          <div className={styles.headerActions}>
            {unreadCount > 0 && (
              <Button
                variant="secondary"
                size="sm"
                onClick={async () => {
                  await markAllAsRead();
                  if (filter === 'unread') void load();
                }}
                tooltip="Отметить прочитанными все уведомления, а не только эту страницу"
              >
                <CheckCheck size={15} aria-hidden />
                Прочитать все
              </Button>
            )}
            <Tooltip content="Настроить, о чём присылать уведомления">
              <Link to="/settings?tab=notifications" className={styles.settingsLink} aria-label="Настройки уведомлений">
                <SettingsIcon size={16} aria-hidden />
              </Link>
            </Tooltip>
          </div>
        </div>

        <div className={styles.filters} role="tablist" aria-label="Фильтр уведомлений">
          {FILTERS.map((f) => (
            <Tooltip key={f.key} content={f.hint}>
              <button
                type="button"
                role="tab"
                aria-selected={filter === f.key}
                className={cn(styles.filterChip, filter === f.key && styles.filterChipActive)}
                onClick={() => handleFilter(f.key)}
              >
                {f.label}
                {f.key === 'unread' && unreadCount > 0 && (
                  <span className={styles.filterCount}>{unreadCount > 99 ? '99+' : unreadCount}</span>
                )}
              </button>
            </Tooltip>
          ))}
        </div>

        {pendingNew > 0 && (
          <button type="button" className={styles.newPill} onClick={() => void load()}>
            <BellRing size={14} aria-hidden />
            {pendingNew === 1 ? 'Новое уведомление' : `Новых уведомлений: ${pendingNew}`} — обновить
          </button>
        )}

        {showSkeleton ? (
          <ul className={styles.list} aria-busy="true">
            {Array.from({ length: 6 }, (_, i) => (
              <li key={i} className={styles.skeletonRow}>
                <Skeleton width={32} height={32} radius="var(--radius-sm)" />
                <div className={styles.skeletonBody}>
                  <Skeleton height={14} width={`${70 - i * 4}%`} />
                  <Skeleton height={11} width="30%" />
                </div>
              </li>
            ))}
          </ul>
        ) : pageError ? (
          <ErrorState message={pageError} onRetry={() => void load()} />
        ) : pageItems.length === 0 ? (
          <EmptyState
            Icon={filter === 'unread' ? Check : Inbox}
            title={filter === 'unread' ? 'Непрочитанных нет' : 'Уведомлений пока нет'}
            hint={
              filter === 'unread'
                ? 'Всё прочитано. Новые события появятся здесь и в колокольчике.'
                : 'Здесь появятся события по вашим заявкам: сообщения, смены статуса, согласования.'
            }
            action={
              filter === 'unread' ? (
                <Button variant="secondary" size="sm" onClick={() => handleFilter('all')}>
                  Показать все
                </Button>
              ) : undefined
            }
          />
        ) : (
          <>
            <div className={cn(styles.feed, pageLoading && styles.feedLoading)} aria-busy={pageLoading}>
              {groups.map((group) => (
                <section key={group.day} className={styles.group}>
                  <h2 className={styles.dayLabel}>{group.day}</h2>
                  <ul className={styles.list}>
                    {group.items.map((n) => {
                      const unread = isUnread(n);
                      const { tone, label } = notificationVisual(n.eventType, n.payload);
                      return (
                        <li key={n.id} className={styles.row}>
                          <Link
                            to={notificationLink(n, userId)}
                            className={cn(styles.item, unread && styles.itemUnread)}
                            onClick={() => unread && markAsRead(n.id)}
                          >
                            <Tooltip content={label}>
                              <span className={cn(styles.eventIcon, styles[`tone-${tone}`])}>
                                <NotificationEventIcon eventType={n.eventType} payload={n.payload} />
                              </span>
                            </Tooltip>
                            <div className={styles.itemBody}>
                              <div className={styles.itemTitle} title={formatNotificationTitle(n)}>{formatNotificationTitle(n)}</div>
                              <div className={styles.itemMeta}>
                                <Tooltip content={formatDateTime(n.createdAt)}>
                                  <time dateTime={n.createdAt}>{formatRelativeTime(n.createdAt)}</time>
                                </Tooltip>
                              </div>
                            </div>
                            {unread && <span className={styles.unreadDot} aria-label="Не прочитано" />}
                          </Link>

                          {unread && (
                            <Tooltip content="Прочитано (не открывая заявку)">
                              <button
                                type="button"
                                className={styles.readBtn}
                                onClick={() => markAsRead(n.id)}
                                aria-label="Отметить прочитанным"
                              >
                                <Check size={15} aria-hidden />
                              </button>
                            </Tooltip>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </section>
              ))}
            </div>

            <Pagination
              page={page}
              totalPages={totalPages}
              total={pageTotal}
              pageSize={PAGE_SIZE}
              itemsLabel="уведомления"
              onChange={(next) => {
                patchParams({ page: next });
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            />
          </>
        )}
      </main>
    </PageMotion>
  );
};
